import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { analiseApi } from '@/lib/api/analise';
import { useAuthStore } from '@/lib/stores/authStore';
import { Info } from '@/design-system/Info';

const JANELAS = [30, 60, 90, 180, 365];

export function FalandoPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const [dias, setDias] = useState(90);

  const { data: temas = [], isLoading } = useQuery({
    queryKey: ['falando', dias],
    queryFn: () => analiseApi.falando(token, dias),
    enabled: !!token,
  });

  // Tema suprimido nao entra em soma nem em maximo: somar o escondido o
  // devolveria por diferenca.
  const visiveis = temas.filter((t) => !t.suprimido);
  const suprimidos = temas.length - visiveis.length;

  const total = visiveis.reduce((a, t) => a + (t.total ?? 0), 0);
  const maxTotal = Math.max(1, ...visiveis.map((t) => t.total ?? 0));
  const emAlta = visiveis.filter((t) => t.variacao !== null && t.variacao > 0).length;
  const totalAbertas = visiveis.reduce((a, t) => a + (t.abertas ?? 0), 0);

  return (
    <div className="page" id="p-falando">
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>O que estão falando da candidata</h1>
          <p>
            Temas mais frequentes nas solicitações que chegam ao atendimento do gabinete, com
            variação em relação ao período anterior de mesmo tamanho.
          </p>
        </div>
      </div>

      {/*
        Fica visível: é declaração de alcance. Escondida atrás do ícone, a tela
        passaria a sugerir uma escuta de redes que a plataforma não faz.
      */}
      <div className="note" style={{ marginBottom: 12, fontSize: 12 }}>
        <b>O que esta tela mede:</b> demandas registradas no atendimento, não menções em redes
        sociais.{' '}
        <Info titulo="De onde vem o que esta tela mede">
          A plataforma não faz escuta de mídias sociais. Nada aqui é inferido de fora do que o
          gabinete efetivamente recebeu.
        </Info>
      </div>

      {/*
        NOTA DE MÉTODO, proto:718 — com o limiar que o código usa.

        O protótipo crava "indicadores só aparecem para recortes com ≥ 50
        pessoas". O sistema não usa 50: `obterK()` lê
        `tenant.configuracoes.k_anonimato` e cai em `K_ANONIMATO_PADRAO = 5`.
        Escrever 50 anunciaria uma proteção quatro vezes maior do que a
        aplicada — e o número é configurável, então cravar QUALQUER literal
        seria errado na maioria dos mandatos.

        A nota vive aqui, e não no Perfil do território como no protótipo,
        porque é aqui (e no painel, e no Agente analista) que a supressão
        acontece: o perfil do território não consome nenhum agregado suprimido.
      */}
      <div
        className="note"
        data-testid="nota-privacidade-agregacao"
        style={{ marginBottom: 12, fontSize: 12 }}
      >
        <b>Privacidade por agregação:</b> tema com menos solicitações que o k-anonimato configurado
        em Administração (padrão 5) aparece como <span className="mono">·</span>, sem barra. O
        número existe e não é zero.{' '}
        <Info titulo="Por que alguns temas aparecem suprimidos">
          A supressão protege quem vê o agregado e não pode abrir os registros: um recorte com uma
          única solicitação apontaria a pessoa que a fez.
        </Info>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Solicitações no período</div>
          <div className="v">{total}</div>
          <div className="d">últimos {dias} dias</div>
        </div>
        <div className="card kpi">
          <div className="l">Temas distintos</div>
          <div className="v">{temas.length}</div>
          <div className="d">categorias com ao menos um registro</div>
        </div>
        <div className="card kpi">
          <div className="l">Temas em alta</div>
          <div className="v" style={{ color: emAlta ? 'var(--warn)' : 'var(--ink)' }}>{emAlta}</div>
          <div className="d">cresceram sobre o período anterior</div>
        </div>
        <div className="card kpi">
          <div className="l">Ainda abertas</div>
          <div className="v" style={{ color: totalAbertas ? 'var(--crit)' : 'var(--good)' }}>
            {totalAbertas}
          </div>
          <div className="d">sem conclusão registrada</div>
        </div>
      </div>

      {/* Janela */}
      <div className="filters">
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Período:</span>
        {JANELAS.map((d) => (
          <span
            key={d}
            className={`chip neutral chipbtn ${dias === d ? 'on' : ''}`}
            onClick={() => setDias(d)}
          >
            {d} dias
          </span>
        ))}
      </div>

      {isLoading && <div className="empty">Apurando os temas...</div>}

      {!isLoading && temas.length === 0 && (
        <div className="empty">
          Nenhuma solicitação registrada nos últimos {dias} dias. Esta tela se popula conforme o
          atendimento do gabinete registra demandas.
        </div>
      )}

      {!isLoading && temas.length > 0 && (
        <div className="card">
          <h2>Ranking de temas</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
            {temas.map((t) => (
              <div key={t.tema}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13 }}>
                  <div>
                    <b>{t.tema}</b>
                    <span style={{ color: 'var(--ink3)', marginLeft: 8, fontSize: 12 }}>
                      {t.suprimido
                        ? 'poucas pessoas neste recorte para exibir os números'
                        : `${t.bairros} território(s) · ${t.abertas} aberta(s) · ${t.concluidas} concluída(s)`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {t.variacao === null ? (
                      <span
                        className="chip neutral"
                        title="Não há período anterior com registros para comparar"
                      >
                        sem base
                      </span>
                    ) : (
                      <span className={`chip ${t.variacao > 0 ? 'warn' : 'good'}`}>
                        {t.variacao > 0 ? '+' : ''}
                        {t.variacao}%
                      </span>
                    )}
                    <b className="num">{t.suprimido ? '·' : t.total}</b>
                  </div>
                </div>

                <div className="bar" style={{ marginTop: 5 }}>
                  {/* Barra proporcional ao total denunciaria por comprimento o
                      numero que o texto omite: no tema suprimido ela nao vai. */}
                  {!t.suprimido && (
                    <i
                      style={{
                        width: `${((t.total ?? 0) / maxTotal) * 100}%`,
                        background: (t.abertas ?? 0) > (t.concluidas ?? 0) ? 'var(--warn)' : 'var(--good)',
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn" onClick={() => navigate('/solicitacoes')}>
              Ver solicitações
            </button>
            <button className="btn pri" onClick={() => navigate('/matriz')}>
              Cruzar com territórios
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
